import { ref, onMounted, onUnmounted, watch, type Ref } from 'vue';
import type { Order } from '@/services/orderService';
import orderService from '@/services/orderService';
import { initializeOrderItems } from '@/utils/kitchenHelpers';

/**
 * Composable for kitchen orders management
 * Implements Single Responsibility Principle - only handles order fetching and auto-refresh
 */
export function useKitchenOrders(isFullscreen: Ref<boolean>) {
  const loading = ref(true);
  const activeOrders = ref<Order[]>([]);
  let refreshInterval: number | null = null;
  let isFetching = ref(false);

  /**
   * Fetch active orders (pending or preparing)
   */
  const fetchActiveOrders = async () => {
    // Prevent concurrent requests
    if (isFetching.value) {
      return;
    }

    try {
      isFetching.value = true;
      loading.value = true;
      const pendingOrders = await orderService.getActiveOrders('pending', undefined, 'kitchen');
      const preparingOrders = await orderService.getActiveOrders('preparing', undefined, 'kitchen');

      // Backend already orders by status (pending first) then by updated_at (FIFO)
      // Just combine the arrays
      const allOrders = [...pendingOrders, ...preparingOrders];

      // Initialize item statuses if not set
      activeOrders.value = allOrders.map(order => initializeOrderItems(order));
    } catch (error) {
      console.error('Error fetching active orders:', error);
    } finally {
      loading.value = false;
      isFetching.value = false;
    }
  };

  /**
   * Set up auto-refresh every 30 seconds when in fullscreen mode
   */
  const setupAutoRefresh = () => {
    // Initial fetch is handled by onMounted, no need to call here
  };

  /**
   * Start auto-refresh interval
   */
  const startAutoRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    // Set up polling every 30 seconds
    refreshInterval = window.setInterval(fetchActiveOrders, 30000);
  };

  /**
   * Stop auto-refresh interval
   */
  const stopAutoRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  };

  /**
   * Clean up interval on unmount
   */
  const cleanup = () => {
    stopAutoRefresh();
  };

  // Watch fullscreen state and control auto-refresh
  watch(isFullscreen, (fullscreen) => {
    if (fullscreen) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
  }, { immediate: true });

  // Lifecycle hooks
  onMounted(() => {
    fetchActiveOrders(); // Initial fetch
    setupAutoRefresh();
  });
  onUnmounted(cleanup);

  return {
    // State
    loading,
    activeOrders,

    // Methods
    fetchActiveOrders,
    cleanup
  };
}
